import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { REACT_APP_DOCS_URL } from '../../constants/config';

const PersonCard = ({ name, title, imageSrc, imageAlt, bio, linkedinUrl }) => {
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const cardNode = cardRef.current;
    if (!cardNode) {
      return undefined;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries, observerInstance) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observerInstance.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 },
    );

    observer.observe(cardNode);

    return () => {
      if (cardNode) {
        observer.unobserve(cardNode);
      }
    };
  }, []);

  return (
    <div ref={cardRef} className={`bg3 rounded-2xl p-6 fade-up ${isVisible ? 'visible' : ''}`}>
      <img className="h-56 w-75 object-cover rounded-xl" src={imageSrc} alt={imageAlt} />
      <div className="mt-4 text-xl font-semibold">{name}</div>
      <div className="text-sm text-gray-500 font-medium">{title}</div>
      <p className="text-gray-600 mt-2 text-sm">{bio}</p>
      <a
        className="text-indigo-600 text-sm font-medium mt-3 inline-flex hover:underline"
        href={linkedinUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        Connect on LinkedIn
      </a>
    </div>
  );
};

PersonCard.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  imageSrc: PropTypes.string.isRequired,
  imageAlt: PropTypes.string.isRequired,
  bio: PropTypes.string.isRequired,
  linkedinUrl: PropTypes.string.isRequired,
};

function About() {
  useEffect(() => {
    const fadeUpElements = document.querySelectorAll('.fade-up');

    const observer = new IntersectionObserver(
      (entries, observerInstance) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observerInstance.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 },
    );

    fadeUpElements.forEach((element) => observer.observe(element));

    return () => {
      fadeUpElements.forEach((element) => observer.unobserve(element));
    };
  }, []);

  return (
    <div className="w-screen bg-white py-20 border-t">
      <div className="content max-w-[1120px] mx-auto pt-[9vh]">
        <section className="bg2 rounded-3xl p-8 md:p-12 fade-up">
          <div className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">About Us</div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mt-4">
            Building pay transparency infrastructure for modern teams
          </h1>
          <p className="text-gray-600 text-lg mt-4 max-w-3xl">
            We&apos;re on a mission to help companies optimise labor costs through AI-native workforce scenario
            planning. Done right, this proactively solves downstream challenges like{' '}
            <Link
              className="text-indigo-600 underline"
              to={`${REACT_APP_DOCS_URL}/news/adjusted-pay-gap-eu-pay-transparency-directive`}
            >
              pay transparency and pay equity reporting
            </Link>{' '}
            in a way that ensures HR compliance with the{' '}
            <Link className="text-indigo-600 underline" to={`${REACT_APP_DOCS_URL}/eu-pay-transparency-directive`}>
              EU Pay Transparency Directive
            </Link>{' '}
            and other global pay equity regulations. We are a team of data scientists and software engineers with deep
            domain expertise in HR, workforce analytics, and data privacy.
          </p>
        </section>

        <section className="mt-12">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl md:text-3xl font-semibold">Founders</h2>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <PersonCard
              name="Siena Duplan"
              title="Co-Founder and CEO"
              imageSrc="/images/people/siena_duplan.png"
              imageAlt="Siena Duplan, CEO and Co-Founder"
              bio="Siena brings a decade of experience in designing and optimizing algorithms, including as the lead data scientist for pay equity algorithms and global pay gap compliance at Salesforce. She holds a Master's degree from Carnegie Mellon University and a B.S. in Statistics from UCLA."
              linkedinUrl="https://www.linkedin.com/in/sienaduplan/"
            />
            <PersonCard
              name="Charlie Oxborough"
              title="Co-Founder and CTO"
              imageSrc="https://github.com/charlie9.png"
              imageAlt="Charlie Oxborough, CTO and Co-Founder"
              bio="Charlie has a distinguished career as a Privacy Engineer at OpenAI and a Lead Software Engineer within the UK Government. She holds a Masters of Engineering in Computer Science from the University of Bristol and is a Chartered Engineer (CEng MIET). Her expertise lies in developing security-first enterprise-grade services for critical domains spanning legal, compliance, and legislative policy."
              linkedinUrl="https://www.linkedin.com/in/charlie-oxborough/"
            />
          </div>
        </section>

        <section className="mt-12">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl md:text-3xl font-semibold">Team</h2>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <PersonCard
              name="Rovina D'Souza"
              title="GTM & Operations"
              imageSrc="/images/people/rovina_dsouza.png"
              imageAlt="Rovina D'Souza, GTM and Operations Lead"
              bio="Rovina brings over a decade of experience scaling strategy and operations across Deloitte, EY, The Access Group, and Salesforce. She has worked at the intersection of consulting, SaaS, and private equity portfolio companies."
              linkedinUrl="https://www.linkedin.com/in/rovinad-souza/"
            />
            <PersonCard
              name="Deanna Gallo"
              title="Consultant"
              imageSrc="/images/people/deanna_gallo.png"
              imageAlt="Deanna Gallo, Consultant"
              bio="Deanna is a leader in people analytics and operations with over a decade of experience building teams and data insights products at Salesforce and Databricks."
              linkedinUrl="https://www.linkedin.com/in/deannagallo/"
            />
          </div>
        </section>

        <section className="mt-12 bg2 rounded-3xl p-8 md:p-12 fade-up">
          <h2 className="text-2xl md:text-3xl font-semibold">Based in San Francisco, operating globally</h2>
          <p className="text-gray-600 mt-3 max-w-3xl">
            We are headquartered in San Francisco, but we keep close to our European roots. We support customers across
            North America, Europe, and APAC by designing workflows that adapt to local reporting rules while keeping a
            unified global view.
          </p>
        </section>

        <section className="mt-12 bg2 rounded-3xl p-8 md:p-12 fade-up">
          <h2 className="text-2xl md:text-3xl font-semibold">Languages Spoken</h2>
          <p className="text-gray-600 mt-3 max-w-3xl">
            Our team supports customers across regions with multilingual coverage.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            {['English', 'Spanish', 'Croatian'].map((language) => (
              <span
                key={language}
                className="rounded-full border border-indigo-200 bg-indigo-50 px-4 py-1 text-sm font-medium text-indigo-700"
              >
                {language}
              </span>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default About;
